import {
  type ComputedDatum,
  type MayHaveLabel,
  type PieCustomLayerProps,
  ResponsivePie,
} from "@nivo/pie";
import { defineMessages, useIntl } from "react-intl";

import Icon from "common/core/icon";
import { Heading } from "common/core/typography";

import Styles from "./completed_details_section.module.scss";
import { ChartsSection } from "./shared/charts_section";
import { CustomLegend } from "./shared/custom_legend";
import { EmptyChart } from "./shared/empty_chart";
import { NumericalChart } from "./shared/numerical_chart";
import { LEGEND_COLORS, type NotarizeCountScalarHash } from "./shared/utils";

const MESSAGES = defineMessages({
  completedTransactions: {
    id: "14e86f2d-7ebb-4a11-bc54-2edcf58ebdf1",
    defaultMessage: "Completed transactions",
  },
  completedTransactionsByType: {
    id: "cd1cdad0-f69e-4a32-9139-0c997d4899d4",
    defaultMessage: "Completed transactions, distribution by type",
  },
  emptyChartHeading: {
    id: "267b2c23-9ea5-4011-8c20-abcefabfb1c5",
    defaultMessage: "No completed transactions",
  },
  emptyCompletedDistributionChartDescription: {
    id: "da9864f6-6dd5-4963-9e41-d5497460eebb",
    defaultMessage: "Completed transactions (Distribution by type) will display here.",
  },
  deltaChartHeading: {
    id: "f150eab1-39c0-46c4-8f9a-9d5c27c9a396",
    defaultMessage: "Completed transactions, current month vs. past month",
  },
  delta: {
    id: "3bc63145-fd48-4730-b31c-bc78881e9fea",
    defaultMessage: "{currentPeriodContent} vs. previous month: {priorPeriodTotalCount}",
  },
  emptyDeltaChartDescription: {
    id: "da9864f6-6dd5-4963-9e41-d5497460eebb",
    defaultMessage: "Completed transactions (Current month vs. past month) will display here.",
  },
  total: {
    id: "81d39640-8e76-4e81-835d-320a758e9e1f",
    defaultMessage: "Total",
  },
});

function CenteredMetric<RawDatum>({
  dataWithArc,
  centerX,
  centerY,
}: PieCustomLayerProps<RawDatum>) {
  const intl = useIntl();
  const total = dataWithArc.reduce(
    (sum: number, datum: ComputedDatum<RawDatum>) =>
      sum + (typeof datum.value === "number" ? datum.value : 0),
    0,
  );

  const lineHeight = 16;

  return (
    <text
      x={centerX}
      y={centerY - lineHeight / 2}
      textAnchor="middle"
      dominantBaseline="central"
      className={Styles.centeredPieMetric}
    >
      <tspan x={centerX} dy={0} className={Styles.centeredPieMetricValue}>
        {intl.formatNumber(total)}
      </tspan>
      <tspan x={centerX} dy={lineHeight}>
        {intl.formatMessage(MESSAGES.total)}
      </tspan>
    </text>
  );
}

export function CompletedDetailsSection({
  completeDistributionReport,
  currentPeriodReport,
  priorPeriodReport,
  last30Days,
  today,
}: {
  completeDistributionReport: NotarizeCountScalarHash[];
  currentPeriodReport: NotarizeCountScalarHash[];
  priorPeriodReport: NotarizeCountScalarHash[];
  last30Days: string;
  today: Date;
}) {
  const intl = useIntl();
  const labelMap = new Map<string, NotarizeCountScalarHash>();
  completeDistributionReport.forEach((item) => {
    const label = String(item.label);
    if (!labelMap.has(label) || (item.count && item.count > 0)) {
      labelMap.set(label, item);
    }
  });

  // handles normalization since period is last 30 days, but report is returned for
  // interval period such as month (default) or daily to achieve a total for 30 days
  const currentPeriodTotalCount = currentPeriodReport.reduce(
    (acc, item) => acc + (item.count ? item.count : 0),
    0,
  );
  const priorPeriodTotalCount = priorPeriodReport.reduce(
    (acc, item) => acc + (item.count ? item.count : 0),
    0,
  );

  const percentageChange = (
    ((currentPeriodTotalCount - priorPeriodTotalCount) / priorPeriodTotalCount) *
    100
  ).toFixed(2);

  // Sorts the labels by count, configs with 0 will be listed at the end
  // Also adds percetage to the legend label
  const formattedCompleteDistributionReport = Array.from(labelMap.values())
    .sort((a, b) => {
      const aCount = a.count ? a.count : 0;
      const bCount = b.count ? b.count : 0;
      if (aCount === 0 && bCount !== 0) {
        return 1;
      }
      if (aCount !== 0 && bCount === 0) {
        return -1;
      }
      return 0;
    })
    .map((item: NotarizeCountScalarHash, i: number) => ({
      id: item.label,
      label: item.label,
      value: item.count,
      color: item.label === "Notarization" ? "var(--caution-80)" : LEGEND_COLORS[i],
      legendLabel: `${item.label}${
        item.count ? `: ${Math.round((item.count / currentPeriodTotalCount) * 100)}%` : ": 0%"
      }`,
    }));

  // Filter out items with value of 0 (since padAngle and others are calculated on each nonEmpty array item)
  const pieChartData = formattedCompleteDistributionReport.filter(
    (item) => item.value && item.value > 0,
  );

  return (
    <ChartsSection
      sectionSize="Large"
      heading={intl.formatMessage(MESSAGES.completedTransactions)}
      description={last30Days}
      firstChart={
        !pieChartData.length ? (
          <EmptyChart
            heading={intl.formatMessage(MESSAGES.emptyChartHeading)}
            description={intl.formatMessage(MESSAGES.emptyCompletedDistributionChartDescription)}
          />
        ) : (
          <>
            <Heading level="h3" textStyle="allCapsLabelSmall">
              {intl.formatMessage(MESSAGES.completedTransactionsByType)}
            </Heading>
            <div className={Styles.pieChartContainer}>
              <ResponsivePie
                data={pieChartData as MayHaveLabel[]}
                margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
                innerRadius={0.5}
                padAngle={0.6}
                cornerRadius={2}
                activeOuterRadiusOffset={8}
                enableArcLabels={false}
                enableArcLinkLabels={false}
                colors={pieChartData.map((item) => item.color)}
                layers={["arcs", "arcLabels", "arcLinkLabels", CenteredMetric]}
              />
            </div>
            <CustomLegend keys={formattedCompleteDistributionReport} />
          </>
        )
      }
      secondChart={
        !currentPeriodTotalCount && !priorPeriodTotalCount ? (
          <EmptyChart
            heading={intl.formatMessage(MESSAGES.emptyChartHeading)}
            description={intl.formatMessage(MESSAGES.emptyDeltaChartDescription)}
          />
        ) : (
          <>
            <Heading level="h3" textStyle="allCapsLabelSmall">
              {intl.formatMessage(MESSAGES.deltaChartHeading)}
            </Heading>
            <NumericalChart
              number={intl.formatNumber(currentPeriodTotalCount)}
              figureLabel={intl.formatMessage(MESSAGES.completedTransactions).toLocaleLowerCase()}
              detail={new Date(today).toLocaleDateString("default", {
                month: "short",
                year: "numeric",
                day: "numeric",
                hour: "numeric",
                minute: "2-digit",
              })}
              subDetail={
                <div className={Styles.numericalChartSubdetail}>
                  {intl.formatMessage(MESSAGES.delta, {
                    currentPeriodContent: (
                      <span
                        className={
                          Number(percentageChange) < 0
                            ? Styles.numericalChartSubdetailDown
                            : Styles.numericalChartSubdetailUp
                        }
                      >
                        <Icon name={Number(percentageChange) < 0 ? "arrow-down" : "arrow-up"} />
                        {Math.abs(Number(percentageChange))}%
                      </span>
                    ),
                    priorPeriodTotalCount,
                  })}
                </div>
              }
            />
          </>
        )
      }
    />
  );
}
